import React, { useEffect, useRef, useState } from "react";
import { HeaderItem, HeaderItemConfig } from "@components/common/Header/utils";
import TextSearchScreen from "@scenes/textSearchScreen/TextSearchScreen";
import { VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import Tooltip from "@components/atoms/Tooltip";
import MoreScreen from "@scenes/more/MoreScreen/MoreScreen";
import { Image, Linking, Platform, Pressable } from "react-native";
import styled, { DefaultTheme, useTheme } from "styled-components/native";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import useMoreScreenModals from "@scenes/more/MoreScreen/useMoreScreenModals";
import { useNavigation, useNavigationState } from "@react-navigation/native";
import usePostOnboarding from "../../../hooks/usePostOnboardingData";
import { actionFunctions } from "@scenes/more/MoreScreen/utils";
import { MenuItemProps } from "@scenes/more/MoreScreen/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch, useSelector } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { useBulkRequestSlice } from "@redux/slices/bulkRequestSlice";
import BulkListSummary from "@components/organisms/BulkListSummary";
import { ProductCategory, ScreenName } from "@utils/constants";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import CustomLogo from "@components/molecules/CustomLogo";
import { findDefaultScreenTab } from "@navigations/utils/convertTabsData";
import { useGetScreenData } from "../../../hooks/useGetScreenData";
import { useBannerData } from "../../../hooks/useBannerData";
import { useGlobalData, useOverlayConfigWeb } from "@redux/slices/globalSlice";
import { useCoachOverlay } from "../../../hooks/useCoachOverlay";
import { useIsPublicAccessRestricted } from "@utils/usePrivateAppGuard";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { useAdBatchTracking } from "../../../hooks/useAdBatchTracking";
import {
  EventType,
  getActiveRouteName,
  getAnalyticsProductData,
} from "@utils/analytics.web";
import { createAdProps } from "@components/molecules/AdView/BannerAd/utils";
import { BannerActionEnum } from "@components/molecules/AdView/BannerAd/types";
import { handleAdLinkPress } from "@utils/product/productValidators";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useOrderCartSlice } from "@redux/slices/orderCartSlice";
import { NavigationHistoryState } from "@components/molecules/Breadcrumbs/utils";
import {
  BuilderAlertManager,
  shouldConfirmHeaderNavigation,
} from "@scenes/jewelry-builder/components/BuilderAlertManager";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { resetFiltersPayload } from "@redux/slices/searchFilters";
import { ActiveButtonsEnum, HeaderSectionProps } from "./types";
import { useAppVisibility } from "../../../hooks/useIsVisible";
import { StoneType } from "@scenes/bulk-request/Utils";

const LOGO_WIDTH = 256;

const HeaderSection = ({
  sectionData,
  sectionName,
  activeButton,
  onPress,
  headerHeight,
  headerBackgroundColor,
}: HeaderSectionProps) => {
  const theme = useTheme();
  const { clearRouteHistory } = useNavigationHistory();
  const { enableJewelryBuilder, isVDBOrg } = useOrgSettingsSlice();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const { organization, headerLogo } = useOrgSettingsSlice();
  const [tooltipIsOpen, setTooltipIsOpen] = React.useState(false);
  const [bulkTotalCount, setBulkTotalCount] = useState(0);
  const { renderModals, showModal } = useMoreScreenModals();
  const { trackButtonEvent, trackAdEvent } = useUserAnalytics() || {};
  const { trackBatchedAdEvent } = useAdBatchTracking();
  const postOnboardingCheck = useSelector(
    (state: any) => state.appState?.postOnboardingCheck
  );
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  let baseTestId = "header-section";
  const navigation = useNavigation();
  const typography = useTypographyStore();
  const dispatch = useDispatch();
  const bulkRequestSlice = useBulkRequestSlice();
  const bulkItemData = bulkRequestSlice.bulkItemData;
  const orderCartSlice = useOrderCartSlice();
  const totalProductCount = bulkRequestSlice.totalProductCount();
  const [singleProductType, setSingleProductType] = useState("");
  const [textSearchInputValue, setTextSearchInputValue] = useState("");
  const tabsData = useGetScreenData(ScreenName.TABS);
  const routeLink = findDefaultScreenTab(tabsData);
  const { currentBanner, banners } = useBannerData();
  const priceMode = userInfo?.user?.price_mode;
  const [isHover, setIsHover] = useState<HeaderItem | null>(null);
  const newRouteName = useNavigationState((state) => getActiveRouteName(state));
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );
  const { shouldHide: shouldHideForPublicUser } = useIsPublicAccessRestricted();
  const lastTrackedBannerUrl = useRef<string | null>(null);
  const isVisible = useAppVisibility();

  const navigateToBulkRequestScreen = (
    product_type: string,
    stone_type?: StoneType
  ) => {
    if (onPress) {
      onPress(null);
    }
    const callback = () => {
      navigate(Routes.BulkRequestNavigator, {
        screen: Routes.BulkRequestScreen,
        params: {
          productType: product_type,
          stone_type: stone_type,
        },
      });
    };
    if (shouldConfirmHeaderNavigation(enableJewelryBuilder, history.length)) {
      BuilderAlertManager.open(() => {
        callback();
      });
    } else {
      if (enableJewelryBuilder) {
        dispatch(resetFiltersPayload());
        if (history.length > 0) {
          // @ts-ignore
          navigation.pop(history.length);
        }
        clearRouteHistory();
      }
      callback();
    }
  };

  const navigateToOrderCartScreen = () => {
    if (onPress) {
      onPress(null);
    }
    const callback = () => {
      navigate(Routes.BulkRequestNavigator, {
        screen: Routes.OrderCartScreen,
      });
    };
    if (shouldConfirmHeaderNavigation(enableJewelryBuilder, history.length)) {
      BuilderAlertManager.open(() => {
        callback();
      });
    } else {
      if (enableJewelryBuilder) {
        dispatch(resetFiltersPayload());
        if (history.length > 0) {
          // @ts-ignore
          navigation.pop(history.length);
        }
        clearRouteHistory();
      }
      callback();
    }
  };

  useEffect(() => {
    const allowedStockTypes = userInfo?.getAllowedStockTypes();
    const bulkItemProductArray = bulkItemData.filter((_item) =>
      allowedStockTypes?.includes(_item.product_type)
    );

    if (bulkItemProductArray?.length === 1) {
      setSingleProductType(bulkItemProductArray[0].product_type);
    }

    const _bulkTotalCount = bulkItemData.reduce((total: number, curr: any) => {
      const count = total + Number(curr.total_count ?? 0);
      return count;
    }, 0);

    if (!orderCartSlice?.orderCartData?.buy_now_session_id) {
      setBulkTotalCount(
        userInfo.getFeatureSettings().enable_order_cart ||
          userInfo.getFeatureSettings().shopping_cart
          ? orderCartSlice?.orderCartData?.total_cart_items
          : _bulkTotalCount
      );
    }
  }, [bulkItemData, orderCartSlice?.orderCartData]);

  const isGuest = useSelector((state: any) => state.userInfo.isGuest);
  const userName = isGuest
    ? t("navigation.profile.general.label.loginRegister", "Login/Register")
    : userInfo?.user?.name;
  const [allowPostOnboadingNavigation, setAllowPostOnboadingNavigation] =
    useState(false);
  const onNavigationSucceeded = (isSucceeded: boolean) => {
    if (isSucceeded) setAllowPostOnboadingNavigation(false);
  };
  usePostOnboarding(allowPostOnboadingNavigation, onNavigationSucceeded);
  const { products } = useGlobalData();
  const getOverlayConfigWeb = useOverlayConfigWeb();
  useCoachOverlay({
    screen: "Header",
    autoStart: true,
    enabled:
      Platform.OS === "web" &&
      (getOverlayConfigWeb?.screens?.Header?.overlayActive === undefined ||
        getOverlayConfigWeb?.screens?.Header?.overlayActive === true) &&
      (userInfo?.onBoardingStatus() || !postOnboardingCheck) &&
      !userInfo.shouldShowAccountProfilePopUp() &&
      newRouteName !== Routes.AccountOverviewScreen,
  });
  const handleLinkPress = async (link?: string, isInternal?: boolean) => {
    const callback = async () => {
      if (link) {
        const supported = await Linking.canOpenURL(link);
        if (isInternal) {
          document.location.href = link;
        } else {
          if (supported) {
            await Linking.openURL(link);
          } else return;
        }
      } else if (
        isCustomApp &&
        organization.redirect_urls.logo_url.length > 0
      ) {
        document.location.href = organization.redirect_urls.logo_url;
      } else {
        navigate(routeLink as never);
      }
    };
    if (shouldConfirmHeaderNavigation(enableJewelryBuilder, history.length)) {
      BuilderAlertManager.open(() => {
        callback();
      });
    } else {
      if (enableJewelryBuilder) {
        dispatch(resetFiltersPayload());
        if (history.length > 0) {
          // @ts-ignore
          navigation.pop(history.length);
        }
        clearRouteHistory();
      }
      callback();
    }
  };

  const showToastWhenBulkListIsEmpty = () => {
    showToast(
      undefined,
      t("bulkRequest.itemsNotAvailableInCart.message.text"),
      ToastTypes.INFO,
      true,
      true
    );
  };

  const renderHeaderItem = (item: HeaderItemConfig) => {
    if (!item) return null;
    const iconPosition = item?.iconPosition === "left" ? "row" : "row-reverse";
    const itemTestId = `${baseTestId}.${item.name}`;
    switch (item.name) {
      case HeaderItem.LOGO:
        return (
          <LogoContainer
            headerHeight={headerHeight}
            testID={itemTestId}
            data-testId={itemTestId}
            onPress={() => {
              handleLinkPress(item?.link, item?.isInternal);
            }}
          >
            {userInfo.isBusinessPlan() ? (
              <CustomLogo />
            ) : userInfo.isProPlan() &&
              userInfo.user?.price_mode !== PriceModes.WHITE ? (
              <CustomLogo />
            ) : isCustomApp ? (
              <CustomLogo />
            ) : (
              priceMode === PriceModes.WHITE && (
                <Logo
                  source={headerLogo ? headerLogo : { uri: item?.url }}
                  resizeMode="cover"
                />
              )
            )}
          </LogoContainer>
        );
      case HeaderItem.ADVERTISEMENT:
        const url = currentBanner?.media_url_url ?? null;
        if (
          isVisible &&
          priceMode === PriceModes.WHITE &&
          url &&
          url !== lastTrackedBannerUrl.current &&
          currentBanner.media_type &&
          (currentBanner?.media_redirect_link ||
            currentBanner?.media_redirect_link_v2)
        ) {
          try {
            trackBatchedAdEvent?.(
              EventType.VIEW_BANNER,
              createAdProps(
                BannerActionEnum.view,
                currentBanner,
                Platform.OS === "web" && getAnalyticsProductData(currentBanner)
              )
            );
          } catch (err) {
            console.error("An error occurred", err);
          }
        }
        lastTrackedBannerUrl.current = url;

        return priceMode === PriceModes.WHITE ? (
          <AdvertisementContainer
            backgroundColor={headerBackgroundColor}
            headerHeight={headerHeight}
            testID={itemTestId}
            data-testId={itemTestId}
          >
            {banners?.map((banner: any, index: number) => {
              return banners?.length > 0 && banner?.media_url_url ? (
                <Pressable
                  key={index}
                  style={{
                    width: "100%",
                    flex: 1,
                    borderRadius: 8,
                    position: "absolute",
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: banner === currentBanner ? 1 : 0,
                    opacity: banner === currentBanner ? 1 : 0,
                  }}
                  onPress={() =>
                    handleAdLinkPress(
                      banner?.media_redirect_link_v2,
                      products,
                      trackAdEvent,
                      currentBanner
                    )
                  }
                >
                  <Image
                    style={{
                      width: "100%",
                      flex: 1,
                      borderRadius: 8,
                    }}
                    source={{ uri: banner.media_url_url }}
                    resizeMode="contain"
                  />
                </Pressable>
              ) : null;
            })}
          </AdvertisementContainer>
        ) : null;
      case HeaderItem.PROFILE:
        return (
          <Tooltip
            showOnPress
            isOpen={tooltipIsOpen}
            setIsOpen={setTooltipIsOpen}
            onClose={() => onPress(null)}
            showArrowIcon={false}
            trigger={() => (
              <HeaderButton
                testID={itemTestId}
                data-testId={itemTestId}
                onHoverIn={() => {
                  setIsHover(HeaderItem.USER_PROFILE);
                }}
                onHoverOut={() => {
                  setIsHover(null);
                }}
                id="profile-Header"
                isActive={
                  activeButton === ActiveButtonsEnum.USER_PROFILE ||
                  newRouteName === Routes.AccountOverviewScreen
                }
                direction={iconPosition}
                isHover={isHover === HeaderItem.USER_PROFILE}
                backgroundColor={headerBackgroundColor}
                onPress={() => {
                  onPress(ActiveButtonsEnum.USER_PROFILE);
                  setTooltipIsOpen(true);
                }}
              >
                <VDBText
                  color={
                    activeButton === ActiveButtonsEnum.USER_PROFILE ||
                    newRouteName === Routes.AccountOverviewScreen
                      ? theme["header-btn-cont-active"]
                      : item?.color ?? theme["header-btn-cont"]
                  }
                  type={typography["Body-M"]}
                >
                  {userName}
                </VDBText>
                <VDBIcon
                  color={
                    userInfo.user?.price_mode === PriceModes.GREEN
                      ? theme["success-01"]
                      : userInfo.user?.price_mode === PriceModes.ORANGE
                      ? theme["warn-01"]
                      : activeButton === ActiveButtonsEnum.USER_PROFILE ||
                        newRouteName === Routes.AccountOverviewScreen
                      ? theme["header-btn-cont-active"]
                      : item?.color || theme["header-btn-cont"]
                  }
                  name={item.icon || "profile"}
                  size={24}
                  variant={IconsVariant.VDBCustom}
                />
              </HeaderButton>
            )}
            tooltipBackgroundColor={theme["over-01"]}
            popoverBackdropColor={"transparent"}
            popoverProps={{ size: "md" }}
            popoverContentProps={{ marginTop: 12 }}
            popoverBodyProps={{ padding: 0, minWidth: 350 }}
          >
            <MoreScreen
              showAsPopover
              onItemClick={(_item: MenuItemProps) => {
                setTooltipIsOpen(false);
                onPress(null);
                actionFunctions({
                  item: _item,
                  dispatch,
                  navigation,
                  onShowSignOutPopup: () => showModal("sign-out-popup"),
                  onShowContactUsModal: () => showModal("contact-us"),
                  onShowJBTModal: () => showModal("jbt"),
                  onShowPostOnboarding: () =>
                    setAllowPostOnboadingNavigation(true),
                  onShowPriceModeModal: () => showModal("price-mode"),

                  onShowTermsAndConditionsModal: () =>
                    showModal("terms-and-conditions"),
                  onShowDeleteAccountModal: () => showModal("delete-account"),
                  onShowDefaultSearchGroupsModal: () =>
                    showModal("default-search-group"),
                  onShowCoachOverlayModal: () => showModal("coach-overlay"),
                  onShowPagesModal: () => showModal("pages", _item?.params),
                  theme,
                  trackButtonEvent,
                });
              }}
            />
          </Tooltip>
        );
      case HeaderItem.SEARCH:
        if (
          (isVDBOrg &&
            !userInfo.user.paid_plan_user &&
            !userInfo.user.trial_plan_user &&
            userInfo?.user?.feature_settings
              ?.display_paid_marketplace_plan_popup) ||
          shouldHideForPublicUser
        ) {
          return null;
        }
        return activeButton === ActiveButtonsEnum.SEARCH_MARKETPLACE ? (
          <TextSearchScreen
            showResultsAsTooltip
            onBackPress={(clearInput: boolean) => {
              onPress(null);
              setIsHover(null);
              if (clearInput) {
                setTextSearchInputValue("");
              }
            }}
            textSearchInputValue={textSearchInputValue}
            setTextSearchInputValue={setTextSearchInputValue}
            customWidth={item?.width}
          />
        ) : (
          <HeaderButton
            id="searchInput"
            testID={itemTestId}
            data-testId={itemTestId}
            isActive={false}
            onPress={() => {
              onPress(ActiveButtonsEnum.SEARCH_MARKETPLACE);
            }}
            direction={iconPosition}
            backgroundColor={headerBackgroundColor}
            onHoverIn={() => {
              setIsHover(HeaderItem.SEARCH_MARKETPLACE);
            }}
            onHoverOut={() => {
              setIsHover(null);
            }}
            isHover={isHover === HeaderItem.SEARCH_MARKETPLACE}
          >
            <VDBText
              color={item?.color ?? theme["header-btn-cont"]}
              type={typography["Body-M"]}
            >
              {t(item?.label || "explore.title.text")}
            </VDBText>
            <VDBIcon
              color={item?.color || theme["header-btn-cont"]}
              name={item?.icon || "main-search"}
              size={24}
              variant={IconsVariant.VDBCustom}
            />
          </HeaderButton>
        );
      case HeaderItem.BULK_LIST:
        return userInfo?.showBulkRequestFeature(
          userInfo
            ?.getAllowedStockTypes()
            ?.find((mItem) =>
              userInfo?.user?.feature_settings?.allowed_bulk_request_types?.includes(
                mItem ?? ""
              )
            ) as ProductCategory,
          userInfo?.user?.price_mode
        ) ? (
          <Tooltip
            showOnPress
            isOpen={activeButton === ActiveButtonsEnum.BULK_LIST}
            setIsOpen={() => {}}
            onTriggerPress={() => {
              if (isGuest) {
                dispatch(setLoginModalType("default"));
              } else {
                onPress(HeaderItem.BULK_LIST);
              }
            }}
            onClose={() => {
              onPress(null);
            }}
            showArrowIcon={false}
            trigger={() => (
              <HeaderButton
                testID={itemTestId}
                data-testId={itemTestId}
                isActive={activeButton === ActiveButtonsEnum.BULK_LIST}
                onHoverIn={() => {
                  setIsHover(HeaderItem.BULK_LIST);
                }}
                onHoverOut={() => {
                  setIsHover(null);
                }}
                isHover={isHover === HeaderItem.BULK_LIST}
                backgroundColor={headerBackgroundColor}
                onPress={() => {
                  if (isGuest) {
                    dispatch(setLoginModalType("default"));
                  } else {
                    if (totalProductCount === 0) {
                      showToastWhenBulkListIsEmpty();
                    } else if (
                      singleProductType !== null &&
                      totalProductCount === 1
                    ) {
                      navigateToBulkRequestScreen(
                        singleProductType,
                        bulkItemData?.[0]?.product_sub_type as StoneType
                      );
                    } else {
                      onPress(ActiveButtonsEnum.BULK_LIST);
                    }
                  }
                }}
                direction={iconPosition}
              >
                <VDBText
                  color={
                    activeButton === ActiveButtonsEnum.BULK_LIST
                      ? theme["header-btn-cont-active"]
                      : item?.color ?? theme["header-btn-cont"]
                  }
                  type={typography["Body-M"]}
                >
                  {t(item?.label || "Bulk List")}
                </VDBText>
                <VDBIcon
                  color={
                    activeButton === ActiveButtonsEnum.BULK_LIST
                      ? theme["header-btn-cont-active"]
                      : item?.color ?? theme["header-btn-cont"]
                  }
                  name={item?.icon || "bulk-list-shopping-cart"}
                  size={24}
                  variant={IconsVariant.VDBCustom}
                />
                {bulkTotalCount > 0 && !isGuest && (
                  <BulkCountBadge>
                    <VDBText
                      type={typography["Minor-M"]}
                      color={theme["cont-00"]}
                    >
                      {bulkTotalCount}
                    </VDBText>
                  </BulkCountBadge>
                )}
              </HeaderButton>
            )}
            tooltipBackgroundColor={theme["over-01"]}
            popoverBackdropColor={"transparent"}
            popoverProps={{ size: "md" }}
            popoverContentProps={{ marginTop: 12 }}
            popoverBodyProps={{ padding: 0, minWidth: 350 }}
          >
            {totalProductCount > 1 && (
              <BulkListSummary
                navigateToBulkScreen={navigateToBulkRequestScreen}
              />
            )}
          </Tooltip>
        ) : null;

      case HeaderItem.ORDER_CART:
        return userInfo.user.feature_settings.enable_order_cart &&
          priceMode !== PriceModes.GREEN ? (
          <HeaderButton
            testID={itemTestId}
            data-testId={itemTestId}
            isActive={
              newRouteName === Routes.OrderCartScreen ||
              newRouteName === Routes.BulkRequestNavigator
            }
            onHoverIn={() => {
              setIsHover(HeaderItem.ORDER_CART);
            }}
            onHoverOut={() => {
              setIsHover(null);
            }}
            isHover={isHover === HeaderItem.ORDER_CART}
            backgroundColor={headerBackgroundColor}
            onPress={() => {
              if (isGuest) {
                dispatch(setLoginModalType("default"));
              } else {
                navigateToOrderCartScreen();
              }
            }}
            direction={iconPosition}
          >
            <VDBText
              color={
                newRouteName === Routes.OrderCartScreen ||
                newRouteName === Routes.BulkRequestNavigator
                  ? theme["header-btn-cont-active"]
                  : item?.color ?? theme["header-btn-cont"]
              }
              type={typography["Body-M"]}
            >
              {t(item?.label || HeaderItem.ORDER_CART)}
            </VDBText>
            <VDBIcon
              color={
                newRouteName === Routes.OrderCartScreen ||
                newRouteName === Routes.BulkRequestNavigator
                  ? theme["header-btn-cont-active"]
                  : item?.color ?? theme["header-btn-cont"]
              }
              name={item?.icon || "bulk-list-shopping-cart"}
              size={24}
              variant={IconsVariant.VDBCustom}
            />
            {bulkTotalCount > 0 && !isGuest && (
              <BulkCountBadge>
                <VDBText type={typography["Minor-M"]} color={theme["cont-00"]}>
                  {bulkTotalCount}
                </VDBText>
              </BulkCountBadge>
            )}
          </HeaderButton>
        ) : null;
      case HeaderItem.SHOPPING_CART:
        return userInfo.user.feature_settings.shopping_cart &&
          priceMode === PriceModes.ORANGE ? (
          <HeaderButton
            testID={itemTestId}
            data-testId={itemTestId}
            isActive={
              newRouteName === Routes.OrderCartScreen ||
              newRouteName === Routes.BulkRequestNavigator
            }
            onHoverIn={() => {
              setIsHover(HeaderItem.SHOPPING_CART);
            }}
            onHoverOut={() => {
              setIsHover(null);
            }}
            isHover={isHover === HeaderItem.SHOPPING_CART}
            backgroundColor={headerBackgroundColor}
            onPress={() => {
              if (isGuest) {
                dispatch(setLoginModalType("default"));
              } else {
                navigateToOrderCartScreen();
              }
            }}
            direction={iconPosition}
          >
            <VDBText
              color={
                newRouteName === Routes.OrderCartScreen ||
                newRouteName === Routes.BulkRequestNavigator
                  ? theme["header-btn-cont-active"]
                  : item?.color ?? theme["header-btn-cont"]
              }
              type={typography["Body-M"]}
            >
              {t(item?.label || HeaderItem.SHOPPING_CART)}
            </VDBText>
            <VDBIcon
              color={
                newRouteName === Routes.OrderCartScreen ||
                newRouteName === Routes.BulkRequestNavigator
                  ? theme["header-btn-cont-active"]
                  : item?.color ?? theme["header-btn-cont"]
              }
              name={item?.icon || "bulk-list-shopping-cart"}
              size={24}
              variant={IconsVariant.VDBCustom}
            />
            {bulkTotalCount > 0 && !isGuest && (
              <BulkCountBadge>
                <VDBText type={typography["Minor-M"]} color={theme["cont-00"]}>
                  {bulkTotalCount}
                </VDBText>
              </BulkCountBadge>
            )}
          </HeaderButton>
        ) : null;

      case HeaderItem.LINK:
        return (
          <HeaderButton
            testID={itemTestId}
            data-testId={itemTestId}
            isActive={false}
            onPress={() => handleLinkPress(item?.link)}
            backgroundColor={headerBackgroundColor}
            direction={iconPosition}
          >
            <VDBText
              color={item?.color ?? theme["header-btn-cont"]}
              type={typography["Body-M"]}
            >
              {t(item?.label || "Bulk List")}
            </VDBText>
            <VDBIcon
              color={item?.color || theme["header-btn-cont"]}
              name={item?.icon || "bulk-list-shopping-cart"}
              size={24}
              variant={IconsVariant.VDBCustom}
            />
          </HeaderButton>
        );
      default:
        return null;
    }
  };

  if (!sectionData || !sectionData?.items || sectionData?.items?.length === 0) {
    return null;
  }

  const searchItem = sectionData.items.find(
    (item: any) => item.name === HeaderItem.SEARCH
  );
  const isSearchActive =
    activeButton === ActiveButtonsEnum.SEARCH_MARKETPLACE && searchItem;

  if (isSearchActive) {
    return renderHeaderItem(searchItem);
  }

  const sortedItems = [...sectionData.items].sort((a: any, b: any) => {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }

    if (a.order !== undefined) {
      return -1;
    }

    if (b.order !== undefined) {
      return 1;
    }

    return sectionData.items.indexOf(a) - sectionData.items.indexOf(b);
  });
  return sortedItems.map((item: any, index: number) => {
    if (item.name === HeaderItem.ADVERTISEMENT) {
    }
    return (
      <React.Fragment key={`${sectionName}-${index}`}>
        {renderHeaderItem(item)}
        {item.name === HeaderItem.PROFILE && renderModals()}
      </React.Fragment>
    );
  });
};

export default HeaderSection;

const LogoContainer = styled.Pressable<{ headerHeight: number }>`
  height: ${({ headerHeight }: { headerHeight: number }) => headerHeight}px;
  width: ${LOGO_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
`;

const AdvertisementContainer = styled.View<{
  backgroundColor?: string;
  headerHeight: number;
}>`
  flex: 1;
  height: ${({ headerHeight }: { headerHeight: number }) =>
    `${headerHeight - 16}px`};
  background-color: ${({
    backgroundColor,
    theme,
  }: {
    backgroundColor: string;
    theme: DefaultTheme;
  }) =>
    (theme[backgroundColor as keyof DefaultTheme] as string) ||
    theme["header-bg-static"]};
  align-items: center;
  max-width: 700px;
  justify-content: center;
  border-radius: 8px;
`;

const HeaderButton = styled.Pressable<{
  isActive: boolean;
  direction: "row" | "row-reverse";
  isHover?: boolean;
  backgroundColor?: string;
}>`
  display: flex;
  flex-direction: ${({ direction }: { direction: "row" | "row-reverse" }) =>
    direction};
  align-items: center;
  background-color: ${({
    theme,
    isActive,
    isHover,
  }: {
    theme: DefaultTheme;
    isActive: boolean;
    isHover: boolean;
  }) =>
    isActive
      ? theme["header-btn-bg-active"]
      : isHover
      ? theme["header-btn-bg-hover"]
      : theme["header-btn-bg-static"]};
  gap: 4px;
  border-radius: 8px;
  border-width: 2px;
  padding: 8px;
  border-color: ${({
    backgroundColor,
    theme,
    isHover,
  }: {
    backgroundColor: string;
    theme: DefaultTheme;
    isHover: boolean;
  }) =>
    backgroundColor || isHover
      ? theme["header-btn-bor-hover"]
      : theme["header-bg-static"]};
`;

const BulkCountBadge = styled.View`
  height: 22px;
  width: 27px;
  background: ${({ theme }: { theme: DefaultTheme }) => theme["error-01"]};
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  position: absolute;
  right: -12px;
  top: -5px;
`;

const Logo = styled.Image`
  height: 100%;
  width: 100%;
`;
